import { useEffect, useRef } from 'react';

/**
 * Fires `onTimeout` if `received` is still false after `timeoutMs`. Cancels/never fires once
 * `received` becomes true. Use this alongside (not instead of) thrown-error handling — a call
 * that succeeds but never delivers the data a flow is waiting on doesn't throw, so an
 * error-counting circuit breaker alone won't catch it. See issue #386 /
 * .claude/skills/silent-incomplete-success (and its ztl-psu-context confirmed incident).
 *
 * This is a deliberate local duplicate of `packages/ui/hooks/useArrivalTimeout.ts`. `src/` (the
 * consultant app) does not yet consume `@financialharmony/ui` — see that package's CLAUDE.md
 * migration roadmap — and adding the dependency just to import this one hook would be a much
 * bigger architectural change than the issue #386 polling audit calls for. Keep the two copies
 * in sync if the contract changes; once `src/` migrates onto `packages/ui`, delete this file and
 * import the shared hook instead.
 *
 * `received` should be `true` whenever the wait is over — either the expected data arrived, or
 * the timeout isn't applicable right now (e.g. polling hasn't started yet).
 */
export function useArrivalTimeout(received: boolean, timeoutMs: number, onTimeout: () => void): void {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (received) return;
    const timer = setTimeout(() => onTimeoutRef.current(), timeoutMs);
    return () => clearTimeout(timer);
  }, [received, timeoutMs]);
}
